/**
 * LOOKING GLASS — immichSlice
 *
 * Remote archive state for the Immich integration. Deliberately SEPARATE from
 * `items`: the local canvas items are IndexedDB-persisted and indexed by the
 * local Fuse.js SearchEngine, while these are transient remote results that
 * must never be pushed through that index or written to IndexedDB.
 *
 * Actions in this slice only MOVE data. Persisting a remote asset onto the
 * canvas is done by the panel calling the existing store actions
 * (addImage / addNote), which already own IndexedDB writes.
 *
 * Merged into the single Zustand create() in src/store/useStore.js the same
 * way spacesSlice is (see the spread on line 26 of that file).
 */
import {
  listAssets, getAsset, listProjects, listTags,
  previewUrl, thumbUrl, assetTitle, formatBytes, formatDate,
  isImageAsset, cardImageFor,
} from '../api/immichClient.js';
import { loadImmichConfig, saveImmichConfig, clearImmichConfig, testImmichConnection } from '../utils/immichConfig.js';

const PAGE_SIZE = 48;

const emptyFilters = () => ({ q: '', project: '', tag: '', type: '', isArchived: null });

/**
 * @param {(partial: object, replace?: boolean) => void} set  Zustand set
 * @param {() => object} get                               Zustand get
 */
export const immichSlice = (set, get) => ({
  // ── Remote archive state ──────────────────────────────────────────────────
  immich: {
    assets: [],
    total: 0,
    page: 1,
    pages: 1,
    projects: [],
    tags: [],
    filters: emptyFilters(),
    loading: false,
    detailLoading: false,
    error: null,
    detail: null,
    connection: null,   // { ok, total?, status?, message? } from the last probe
    config: { baseUrl: '', apiKey: '' },
    // Local-only bookkeeping, NOT persisted.
    importingIds: [],
    importedIds: [],
  },

  // ── Config ────────────────────────────────────────────────────────────────

  /** Pull the persisted bridge config into the store (called when the panel opens). */
  initImmich: () => {
    set((s) => ({ immich: { ...s.immich, config: loadImmichConfig() } }));
  },

  saveImmichConfig: (partial) => {
    const current = get().immich.config;
    const saved = saveImmichConfig({
      baseUrl: partial.baseUrl ?? current.baseUrl,
      apiKey: partial.apiKey ?? current.apiKey,
    });
    set((s) => ({
      immich: {
        ...s.immich,
        config: saved,
        connection: null,      // invalidate the previous probe result
        assets: [], total: 0, page: 1, pages: 1, detail: null, error: null,
      },
    }));
    return saved;
  },

  clearImmichConfig: () => {
    const cleared = clearImmichConfig();
    set((s) => ({
      immich: {
        ...s.immich,
        config: cleared,
        connection: null,
        assets: [], total: 0, page: 1, pages: 1, detail: null, error: null,
      },
    }));
    return cleared;
  },

  testImmich: async () => {
    const { config } = get().immich;
    const result = await testImmichConnection({ baseUrl: config.baseUrl, apiKey: config.apiKey });
    set((s) => ({ immich: { ...s.immich, connection: result } }));
    return result;
  },

  // ── Filters + fetch ───────────────────────────────────────────────────────

  setImmichFilters: (partial) => {
    set((s) => ({
      immich: { ...s.immich, filters: { ...s.immich.filters, ...partial }, page: 1 },
    }));
    return get().loadImmichAssets();
  },

  /** Load a page of assets honouring the current filters. */
  loadImmichAssets: async (pageOverride) => {
    const { filters } = get().immich;
    const page = pageOverride || get().immich.page || 1;
    set((s) => ({ immich: { ...s.immich, loading: true, error: null } }));

    const res = await listAssets({
      q: filters.q,
      project: filters.project,
      tag: filters.tag,
      type: filters.type,
      isArchived: filters.isArchived,
      page,
      limit: PAGE_SIZE,
    });

    if (!res.ok) {
      set((s) => ({
        immich: { ...s.immich, loading: false, assets: [], total: 0, pages: 1, error: res.message },
      }));
      return res;
    }

    set((s) => ({
      immich: {
        ...s.immich,
        loading: false,
        error: null,
        assets: res.data,
        total: res.meta?.total ?? res.data.length,
        page: res.meta?.page ?? page,
        pages: res.meta?.pages ?? 1,
      },
    }));
    return res;
  },

  goToImmichPage: (page) => {
    const { pages } = get().immich;
    const target = Math.min(Math.max(1, Number(page) || 1), Math.max(1, pages));
    set((s) => ({ immich: { ...s.immich, page: target } }));
    return get().loadImmichAssets(target);
  },

  /** Load the project + tag facet lists used by the filter row. */
  loadImmichFacets: async () => {
    const [projects, tags] = await Promise.all([listProjects(), listTags()]);
    set((s) => ({
      immich: {
        ...s.immich,
        projects: projects.ok ? projects.data : s.immich.projects,
        tags: tags.ok ? tags.data : s.immich.tags,
        error: projects.ok && tags.ok ? null : (projects.message || tags.message || null),
      },
    }));
    return { projects, tags };
  },

  /** Fetch the full record for one asset (tags, projects, ai_metadata). */
  openImmichAsset: async (id) => {
    set((s) => ({ immich: { ...s.immich, detailLoading: true, detail: null } }));
    const res = await getAsset(id);
    if (!res.ok) {
      set((s) => ({ immich: { ...s.immich, detailLoading: false, error: res.message } }));
      return res;
    }
    set((s) => ({ immich: { ...s.immich, detailLoading: false, detail: res.data } }));
    return res;
  },

  closeImmichAsset: () => set((s) => ({ immich: { ...s.immich, detail: null } })),

  // ── Import bookkeeping ────────────────────────────────────────────────────

  /** Guard so a double-tap can't spawn the same card twice while in flight. */
  markImmichImporting: (id) => set((s) => ({
    immich: s.immich.importingIds.includes(id)
      ? s.immich
      : { ...s.immich, importingIds: [...s.immich.importingIds, id] },
  })),

  finishImmichImport: (id, succeeded) => set((s) => ({
    immich: {
      ...s.immich,
      importingIds: s.immich.importingIds.filter((x) => x !== id),
      importedIds: succeeded && !s.immich.importedIds.includes(id)
        ? [...s.immich.importedIds, id]
        : s.immich.importedIds,
    },
  })),

  isImmichImported: (id) => get().immich.importedIds.includes(id),
});

/** Re-exported so the panel gets display helpers without a second import path. */
export { previewUrl, thumbUrl, assetTitle, formatBytes, formatDate, isImageAsset, cardImageFor };